import {
  getConfiguratorData,
  getConfiguratorDataForProduct,
  getProductConfiguratorConfigs,
  LOCAL_CONFIGURATOR_DATA,
  type ConfiguratorData,
} from './configurator-data';

const CONFIGURATOR_CURRENCY = 'EUR';

export type HolePositionOverride = {
  xMm: number;
  yMm: number;
};

export type HoleShape = 'round' | 'slot' | 'rect';
export type HoleSlotOrientation = 'horizontal' | 'vertical' | 'custom';
export type InternalCutShape = 'slot' | 'round' | 'rect';
export type BendOrientation = 'horizontal' | 'vertical';
export type BendDirection = 'up' | 'down';
export type BendPositionMode = 'manual' | 'partCenter' | 'featureCenter' | 'betweenFeatures';
export type BendPositionAnchor = 'center' | 'start' | 'end';
export type OutlineNotchEdge = 'top' | 'right' | 'bottom' | 'left';

export type BendPositionConstraint = {
  type?: BendPositionMode;
  featureId?: string;
  featureIds?: string[];
  axis?: 'x' | 'y';
  anchor?: BendPositionAnchor;
  offsetMm?: number;
};

export type OutlineNotchOverride = {
  id: string;
  label?: string;
  edge?: OutlineNotchEdge;
  centerMm?: number;
  centerRatio?: number;
  widthMm?: number;
  depthMm?: number;
  radiusMm?: number;
  mouthRadiusMm?: number;
};

export type BendLineOverride = {
  id: string;
  label?: string;
  orientation?: BendOrientation;
  positionMm?: number;
  positionRatio?: number;
  positionMode?: BendPositionMode;
  constraint?: BendPositionConstraint;
  angleDeg?: number;
  direction?: BendDirection;
};

export type InternalCutOverride = {
  id: string;
  label?: string;
  shape?: InternalCutShape;
  xMm?: number;
  yMm?: number;
  xRatio?: number;
  yRatio?: number;
  widthMm?: number;
  lengthMm?: number;
  orientation?: HoleSlotOrientation;
  angleDeg?: number;
  cornerRadiusMm?: number;
};

export type CornerTreatmentType = 'square' | 'chamfer' | 'chamfer-irregular' | 'radius';
export type HolePatternOrientation = 'horizontal' | 'vertical';

type ConfiguratorDefaults = {
  productTypeId: string;
  machineId: string;
  widthMm: number;
  lengthMm: number;
  thicknessMm: number;
  flangeMm: number;
  bendAngleDeg: number;
  finishId: string;
  holeCount: number;
  holeDiameterMm: number;
  holeShape: HoleShape;
  holeSlotLengthMm: number;
  holeSlotOrientation: HoleSlotOrientation;
  holeSlotAngleDeg: number;
  holePatternId: string;
  holePatternEdgeXMm: number;
  holePatternEdgeYMm: number;
  holePatternOrientation: HolePatternOrientation;
  materialId: string;
  quantity: number;
  edgeRadiusMm: number;
  cornerTopLeftType: CornerTreatmentType;
  cornerTopLeftMm: number;
  cornerTopLeftChamferXMm: number;
  cornerTopLeftChamferYMm: number;
  cornerTopRightType: CornerTreatmentType;
  cornerTopRightMm: number;
  cornerTopRightChamferXMm: number;
  cornerTopRightChamferYMm: number;
  cornerBottomRightType: CornerTreatmentType;
  cornerBottomRightMm: number;
  cornerBottomRightChamferXMm: number;
  cornerBottomRightChamferYMm: number;
  cornerBottomLeftType: CornerTreatmentType;
  cornerBottomLeftMm: number;
  cornerBottomLeftChamferXMm: number;
  cornerBottomLeftChamferYMm: number;
  sheetPresetId: string;
  sheetWidthMm: number;
  sheetLengthMm: number;
  nestingGapMm: number;
  holePositions?: HolePositionOverride[];
  holeDiameters?: number[];
  holeShapes?: HoleShape[];
  holeSlotLengths?: number[];
  holeSlotOrientations?: HoleSlotOrientation[];
  holeSlotAnglesDeg?: number[];
  outlineNotches?: OutlineNotchOverride[];
  internalCuts?: InternalCutOverride[];
  bendLines?: BendLineOverride[];
};

export type ConfiguratorState = ConfiguratorDefaults & {
  configurationName?: string;
  holeSlotAngleDeg?: number;
  outlineOuterRadiusMm?: number;
  outlineCenterGapMm?: number;
  outlineTopNotchDepthMm?: number;
  outlineNotches?: OutlineNotchOverride[];
  internalCuts?: InternalCutOverride[];
  bendLines?: BendLineOverride[];
};

export type ConfiguratorEstimate = {
  areaM2: number;
  weightKg: number;
  totalWeightKg: number;
  unitPrice: number;
  totalPrice: number;
  cutLengthM: number;
  leadTimeDays: number;
};

export type ProductBendLine = {
  id: string;
  label: string;
  orientation: BendOrientation;
  positionMm: number;
  positionMode: BendPositionMode;
  constraint?: BendPositionConstraint;
  angleDeg: number;
  direction: BendDirection;
};

export type ProductInternalCut = {
  id: string;
  label: string;
  shape: InternalCutShape;
  xMm: number;
  yMm: number;
  xRatio: number;
  yRatio: number;
  widthMm: number;
  lengthMm: number;
  orientation: HoleSlotOrientation;
  angleDeg: number;
  cornerRadiusMm: number;
};

export type ProductOutlineNotch = {
  id: string;
  label: string;
  edge: OutlineNotchEdge;
  centerMm: number;
  centerRatio: number;
  widthMm: number;
  depthMm: number;
  radiusMm: number;
  mouthRadiusMm: number;
};

export type HolePoint = {
  xMm: number;
  yMm: number;
  xRatio: number;
  yRatio: number;
  diameterMm: number;
  shape: HoleShape;
  slotLengthMm: number;
  slotWidthMm: number;
  slotOrientation: HoleSlotOrientation;
  slotAngleDeg: number;
};

type RawHolePoint = Pick<HolePoint, 'xMm' | 'yMm' | 'xRatio' | 'yRatio'>;

export type GeometryCheck = {
  edgeDistanceMm: number;
  pitchXMm: number | null;
  pitchYMm: number | null;
  minRequiredEdgeMm: number;
  minRequiredPitchMm: number;
  minRequiredHoleMm: number;
  manufacturabilityScore: number;
  warnings: string[];
};

type LimitKey = keyof typeof LOCAL_CONFIGURATOR_DATA.limits;
type CuttingProcess = 'laser' | 'plasma' | 'drilling';

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function roundGeometryMm(value: number) {
  return Math.round(value * 1000) / 1000;
}

export function getHoleDiameter(config: ConfiguratorState, index: number) {
  const limit = getEffectiveLimits(config).holeDiameterMm;
  const value = config.holeDiameters?.[index] ?? config.holeDiameterMm;

  return clamp(value, limit.min, limit.max);
}

export function getHoleShape(config: ConfiguratorState, index: number): HoleShape {
  const value = config.holeShapes?.[index] ?? config.holeShape;
  if (value === 'slot' || value === 'rect') return value;
  return 'round';
}

export function getHoleSlotOrientation(config: ConfiguratorState, index = 0): HoleSlotOrientation {
  const value = config.holeSlotOrientations?.[index] ?? config.holeSlotOrientation;
  if (value === 'horizontal' || value === 'custom') return value;
  return 'vertical';
}

export function getHoleSlotAngleDeg(config: ConfiguratorState, index = 0) {
  const orientation = getHoleSlotOrientation(config, index);
  if (orientation === 'horizontal') return 0;
  if (orientation === 'vertical') return 90;

  const value = config.holeSlotAnglesDeg?.[index] ?? config.holeSlotAngleDeg ?? 45;
  return clamp(value, -180, 180);
}

export function getHoleSlotLength(config: ConfiguratorState, index: number, widthMm?: number) {
  const limit = getEffectiveLimits(config).holeSlotLengthMm;
  const slotWidthMm = widthMm ?? getHoleDiameter(config, index);
  const value = config.holeSlotLengths?.[index] ?? config.holeSlotLengthMm;

  return clamp(Math.max(value, slotWidthMm), Math.max(limit.min, slotWidthMm), limit.max);
}

export function getHoleHalfExtents(
  hole: Pick<
    HolePoint,
    'shape' | 'slotLengthMm' | 'slotWidthMm' | 'diameterMm' | 'slotOrientation'
  > &
    Partial<Pick<HolePoint, 'slotAngleDeg'>>,
) {
  if (hole.shape === 'round') {
    const radius = hole.diameterMm / 2;
    return { xMm: radius, yMm: radius };
  }

  const widthMm = Math.max(hole.slotWidthMm, 0);
  const lengthMm = Math.max(hole.slotLengthMm, widthMm);

  const angleDeg =
    typeof hole.slotAngleDeg === 'number'
      ? hole.slotAngleDeg
      : hole.slotOrientation === 'horizontal'
        ? 0
        : 90;
  const angle = (angleDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(angle));
  const sin = Math.abs(Math.sin(angle));

  return {
    xMm: (lengthMm / 2) * cos + (widthMm / 2) * sin,
    yMm: (lengthMm / 2) * sin + (widthMm / 2) * cos,
  };
}

export function getHoleAreaMm2(
  hole: Pick<HolePoint, 'shape' | 'slotLengthMm' | 'slotWidthMm' | 'diameterMm'>,
) {
  if (hole.shape === 'round') return Math.PI * Math.pow(hole.diameterMm / 2, 2);

  const widthMm = Math.max(hole.slotWidthMm, 0);
  const lengthMm = Math.max(hole.slotLengthMm, widthMm);
  if (hole.shape === 'rect') return widthMm * lengthMm;

  const straightMm = Math.max(lengthMm - widthMm, 0);

  return straightMm * widthMm + Math.PI * Math.pow(widthMm / 2, 2);
}

export function getHoleCutLengthMm(
  hole: Pick<HolePoint, 'shape' | 'slotLengthMm' | 'slotWidthMm' | 'diameterMm'>,
) {
  if (hole.shape === 'round') return Math.PI * hole.diameterMm;

  const widthMm = Math.max(hole.slotWidthMm, 0);
  const lengthMm = Math.max(hole.slotLengthMm, widthMm);
  if (hole.shape === 'rect') return 2 * (widthMm + lengthMm);

  const straightMm = Math.max(lengthMm - widthMm, 0);

  return straightMm * 2 + Math.PI * widthMm;
}

export function hasMixedHoleDiameters(config: ConfiguratorState) {
  if (config.holeCount <= 1) return false;

  const diameters = Array.from({ length: config.holeCount }, (_, index) =>
    getHoleDiameter(config, index),
  );

  return new Set(diameters.map(value => Math.round(value * 1000))).size > 1;
}

function withHoleOverrides(config: ConfiguratorState, points: RawHolePoint[]): HolePoint[] {
  return points.map((point, index) => {
    const diameterMm = getHoleDiameter(config, index);
    const shape = getHoleShape(config, index);
    const slotLengthMm = getHoleSlotLength(config, index, diameterMm);
    const slotOrientation = getHoleSlotOrientation(config, index);
    const override = config.holePositions?.[index];
    const sourcePoint = override ?? point;
    const xMm = roundGeometryMm(clamp(sourcePoint.xMm, 0, config.widthMm));
    const yMm = roundGeometryMm(clamp(sourcePoint.yMm, 0, config.lengthMm));

    return {
      xMm,
      yMm,
      xRatio: xMm / Math.max(config.widthMm, 1),
      yRatio: yMm / Math.max(config.lengthMm, 1),
      diameterMm,
      shape,
      slotLengthMm,
      slotWidthMm: diameterMm,
      slotOrientation,
      slotAngleDeg: getHoleSlotAngleDeg(config, index),
    };
  });
}

function byId<T extends { id: string }>(items: T[], id: string) {
  return items.find(item => item.id === id) ?? items[0];
}

type ConfiguratorProduct = ConfiguratorData['product'] & {
  defaults?: Partial<ConfiguratorState>;
};

export function getProductOptions(): ConfiguratorProduct[] {
  const configs = getProductConfiguratorConfigs();
  const products = configs.flatMap(config => {
    const relatedProducts =
      'relatedProducts' in config
        ? (config.relatedProducts as unknown as ConfiguratorProduct[])
        : [];
    const product: ConfiguratorProduct = {
      ...(config.product as ConfiguratorProduct),
      defaults: config.defaults as unknown as Partial<ConfiguratorState>,
    };

    return [product, ...relatedProducts];
  });
  const seen = new Set<string>();

  return products.filter(product => {
    if (seen.has(product.id)) return false;
    seen.add(product.id);
    return true;
  });
}

export function getSelectedOptions(config: ConfiguratorState) {
  const data = getConfiguratorDataForProduct(config.productTypeId);

  return {
    product: byId(getProductOptions(), config.productTypeId),
    machine: byId(data.machines, config.machineId),
    finish: byId(data.finishes, config.finishId),
    material: byId(data.materials, config.materialId),
    holePattern: byId(data.holePatterns, config.holePatternId),
  };
}

export function productSupportsFeature(config: ConfiguratorState, feature: string) {
  return getSelectedOptions(config).product.features.includes(feature);
}

type BendPositionSource = BendLineOverride & {
  id?: string;
  label?: string;
  orientation?: string;
  direction?: string;
};

type BendFeaturePosition = {
  id: string;
  xMm: number;
  yMm: number;
  widthMm: number;
  lengthMm: number;
  orientation?: HoleSlotOrientation;
};

function getBendFeaturePositions(config: ConfiguratorState): BendFeaturePosition[] {
  const internalCuts = getProductInternalCuts(config).map(cut => {
    const isHorizontal = cut.orientation === 'horizontal';
    const isVertical = cut.orientation === 'vertical';
    const widthOnX = isHorizontal ? cut.lengthMm : isVertical ? cut.widthMm : cut.lengthMm;
    const lengthOnY = isVertical ? cut.lengthMm : isHorizontal ? cut.widthMm : cut.lengthMm;

    return {
      id: cut.id,
      xMm: cut.xMm,
      yMm: cut.yMm,
      widthMm: widthOnX,
      lengthMm: lengthOnY,
      orientation: cut.orientation,
    };
  });

  const outlineNotches = getProductOutlineNotches(config).map(notch => {
    const isHorizontalEdge = notch.edge === 'top' || notch.edge === 'bottom';
    const xMm = isHorizontalEdge
      ? notch.centerMm
      : notch.edge === 'left'
        ? notch.depthMm / 2
        : config.widthMm - notch.depthMm / 2;
    const yMm = isHorizontalEdge
      ? notch.edge === 'top'
        ? notch.depthMm / 2
        : config.lengthMm - notch.depthMm / 2
      : notch.centerMm;

    return {
      id: notch.id,
      xMm,
      yMm,
      widthMm: isHorizontalEdge ? notch.widthMm : notch.depthMm,
      lengthMm: isHorizontalEdge ? notch.depthMm : notch.widthMm,
    };
  });

  const holes = getHoleLayout(config).map((hole, index) => ({
    id: `hole-${index + 1}`,
    xMm: hole.xMm,
    yMm: hole.yMm,
    widthMm: hole.diameterMm,
    lengthMm: hole.diameterMm,
  }));

  return [...internalCuts, ...outlineNotches, ...holes];
}

function getFeaturePositionOnAxis(
  feature: BendFeaturePosition,
  axis: 'x' | 'y',
  anchor: BendPositionAnchor,
) {
  const center = axis === 'x' ? feature.xMm : feature.yMm;
  const size = axis === 'x' ? feature.widthMm : feature.lengthMm;

  if (anchor === 'start') return center - size / 2;
  if (anchor === 'end') return center + size / 2;
  return center;
}

function getManualBendPositionMm(
  line: BendPositionSource,
  index: number,
  lineCount: number,
  axisSize: number,
) {
  const ratio =
    typeof line.positionRatio === 'number'
      ? clamp(line.positionRatio, 0, 1)
      : (index + 1) / (lineCount + 1);

  return typeof line.positionMm === 'number'
    ? clamp(line.positionMm, 0, axisSize)
    : axisSize * ratio;
}

function resolveBendPositionMm(
  config: ConfiguratorState,
  line: BendPositionSource,
  index: number,
  lineCount: number,
  orientation: BendOrientation,
  axisSize: number,
) {
  const axis = orientation === 'vertical' ? 'x' : 'y';
  const constraint = line.constraint;
  const mode: BendPositionMode = line.positionMode ?? constraint?.type ?? 'manual';
  const offsetMm = constraint?.offsetMm ?? 0;

  if (mode === 'partCenter') {
    return clamp(axisSize / 2 + offsetMm, 0, axisSize);
  }

  if (mode === 'featureCenter' && (constraint?.featureId || line.id)) {
    const featureId = constraint?.featureId ?? line.id;
    const feature = getBendFeaturePositions(config).find(item => item.id === featureId);
    if (feature) {
      return clamp(
        getFeaturePositionOnAxis(feature, axis, constraint?.anchor ?? 'center') + offsetMm,
        0,
        axisSize,
      );
    }
  }

  if (mode === 'betweenFeatures' && constraint?.featureIds?.length) {
    const features = getBendFeaturePositions(config).filter(item =>
      constraint.featureIds?.includes(item.id),
    );
    if (features.length > 0) {
      const average =
        features.reduce(
          (sum, feature) =>
            sum + getFeaturePositionOnAxis(feature, axis, constraint?.anchor ?? 'center'),
          0,
        ) / features.length;

      return clamp(average + offsetMm, 0, axisSize);
    }
  }

  return getManualBendPositionMm(line, index, lineCount, axisSize);
}

export function getProductBendLines(config: ConfiguratorState): ProductBendLine[] {
  const visual = getSelectedOptions(config).product.visual as {
    bendLines?: BendPositionSource[];
  };
  const sourceLines = config.bendLines ?? visual.bendLines ?? [];

  return sourceLines.map((line, index) => {
    const orientation = line.orientation === 'vertical' ? 'vertical' : 'horizontal';
    const axisSize = orientation === 'vertical' ? config.widthMm : config.lengthMm;
    const positionMm = resolveBendPositionMm(
      config,
      line,
      index,
      sourceLines.length,
      orientation,
      axisSize,
    );
    const positionMode: BendPositionMode = line.positionMode ?? line.constraint?.type ?? 'manual';

    return {
      id: line.id ?? `bend-${index + 1}`,
      label: line.label ?? `Ohyb ${index + 1}`,
      orientation,
      positionMm,
      positionMode,
      constraint: line.constraint,
      angleDeg: Math.min(Math.max(line.angleDeg ?? config.bendAngleDeg ?? 90, 0), 180),
      direction: line.direction === 'down' ? 'down' : 'up',
    };
  });
}

export function getProductOutlineSettings(config: ConfiguratorState) {
  const visual = getSelectedOptions(config).product.visual as {
    outerRadiusMm?: number;
    centerGapMm?: number;
    topNotchDepthMm?: number;
  };

  return {
    outerRadiusMm: Math.max(
      0,
      Math.min(
        config.outlineOuterRadiusMm ?? visual.outerRadiusMm ?? config.edgeRadiusMm ?? 0,
        Math.min(config.widthMm, config.lengthMm) / 2,
      ),
    ),
    centerGapMm: Math.max(
      0,
      Math.min(config.outlineCenterGapMm ?? visual.centerGapMm ?? 0, config.widthMm * 0.45),
    ),
    topNotchDepthMm: Math.max(
      0,
      Math.min(config.outlineTopNotchDepthMm ?? visual.topNotchDepthMm ?? 0, config.lengthMm * 0.6),
    ),
  };
}

export function getProductOutlineNotches(config: ConfiguratorState): ProductOutlineNotch[] {
  const visual = getSelectedOptions(config).product.visual as {
    centerGapMm?: number;
    topNotchDepthMm?: number;
    outlineNotches?: OutlineNotchOverride[];
  };
  const outline = getProductOutlineSettings(config);
  const fallbackNotches =
    outline.centerGapMm > 0 && outline.topNotchDepthMm > 0
      ? [
          {
            id: 'top-notch',
            label: 'Horný zárez',
            edge: 'top' as const,
            centerMm: config.widthMm / 2,
            centerRatio: 0.5,
            widthMm: outline.centerGapMm,
            depthMm: outline.topNotchDepthMm,
            radiusMm: 0,
            mouthRadiusMm: 5,
          },
        ]
      : [];
  const sourceNotches = config.outlineNotches ?? visual.outlineNotches ?? fallbackNotches;

  return sourceNotches
    .map((notch, index) => {
      const edge: OutlineNotchEdge =
        notch.edge === 'right' || notch.edge === 'bottom' || notch.edge === 'left'
          ? notch.edge
          : 'top';
      const isHorizontalEdge = edge === 'top' || edge === 'bottom';
      const axisSize = isHorizontalEdge ? config.widthMm : config.lengthMm;
      const depthAxis = isHorizontalEdge ? config.lengthMm : config.widthMm;
      const widthMm = Math.max(0, Math.min(notch.widthMm ?? outline.centerGapMm, axisSize * 0.85));
      const depthMm = Math.max(
        0,
        Math.min(notch.depthMm ?? outline.topNotchDepthMm, depthAxis * 0.75),
      );
      const centerMm =
        typeof notch.centerMm === 'number'
          ? notch.centerMm
          : axisSize * Math.min(Math.max(notch.centerRatio ?? 0.5, 0), 1);
      const clampedCenter = Math.min(
        Math.max(centerMm, widthMm / 2),
        Math.max(widthMm / 2, axisSize - widthMm / 2),
      );
      const leftSpace = clampedCenter - widthMm / 2;
      const rightSpace = axisSize - (clampedCenter + widthMm / 2);
      const radiusMm = Math.max(0, Math.min(notch.radiusMm ?? 0, widthMm * 0.45, depthMm * 0.45));
      const mouthRadiusMm = Math.max(
        0,
        Math.min(notch.mouthRadiusMm ?? 0, depthMm * 0.45, leftSpace, rightSpace),
      );

      return {
        id: notch.id ?? `outline-notch-${index + 1}`,
        label: notch.label ?? `Zárez ${index + 1}`,
        edge,
        centerMm: roundGeometryMm(clampedCenter),
        centerRatio: clampedCenter / Math.max(axisSize, 1),
        widthMm: roundGeometryMm(widthMm),
        depthMm: roundGeometryMm(depthMm),
        radiusMm: roundGeometryMm(radiusMm),
        mouthRadiusMm: roundGeometryMm(mouthRadiusMm),
      };
    })
    .filter(notch => notch.widthMm > 0 && notch.depthMm > 0);
}

export function getProductInternalCuts(config: ConfiguratorState): ProductInternalCut[] {
  const visual = getSelectedOptions(config).product.visual as {
    internalCuts?: Array<{
      id?: string;
      label?: string;
      shape?: string;
      xMm?: number;
      yMm?: number;
      xRatio?: number;
      yRatio?: number;
      widthMm?: number;
      lengthMm?: number;
      orientation?: string;
      angleDeg?: number;
      cornerRadiusMm?: number;
    }>;
  };
  const sourceCuts = config.internalCuts ?? visual.internalCuts ?? [];

  return sourceCuts.map((cut, index) => {
    const xRatio = typeof cut.xRatio === 'number' ? Math.min(Math.max(cut.xRatio, 0), 1) : 0.5;
    const yRatio = typeof cut.yRatio === 'number' ? Math.min(Math.max(cut.yRatio, 0), 1) : 0.5;
    const xMm =
      typeof cut.xMm === 'number'
        ? Math.min(Math.max(cut.xMm, 0), config.widthMm)
        : config.widthMm * xRatio;
    const yMm =
      typeof cut.yMm === 'number'
        ? Math.min(Math.max(cut.yMm, 0), config.lengthMm)
        : config.lengthMm * yRatio;
    const widthMm = Math.max(0.5, cut.widthMm ?? config.thicknessMm);
    const shape = cut.shape === 'round' ? 'round' : cut.shape === 'rect' ? 'rect' : 'slot';
    const lengthMm = shape === 'round' ? widthMm : Math.max(widthMm, cut.lengthMm ?? widthMm);
    const maxCornerRadiusMm = Math.min(widthMm, lengthMm) / 2;
    const cornerRadiusMm = clamp(
      cut.cornerRadiusMm ?? (shape === 'rect' ? 0 : maxCornerRadiusMm),
      0,
      maxCornerRadiusMm,
    );
    const orientation: HoleSlotOrientation =
      cut.orientation === 'horizontal' || cut.orientation === 'custom'
        ? cut.orientation
        : 'vertical';
    const angleDeg =
      orientation === 'horizontal'
        ? 0
        : orientation === 'vertical'
          ? 90
          : clamp(cut.angleDeg ?? 45, -180, 180);

    const roundedX = roundGeometryMm(xMm);
    const roundedY = roundGeometryMm(yMm);
    const roundedWidth = roundGeometryMm(widthMm);
    const roundedLength = roundGeometryMm(lengthMm);

    return {
      id: cut.id ?? `internal-cut-${index + 1}`,
      label: cut.label ?? `Rez ${index + 1}`,
      shape,
      xMm: roundedX,
      yMm: roundedY,
      xRatio: roundedX / Math.max(config.widthMm, 1),
      yRatio: roundedY / Math.max(config.lengthMm, 1),
      widthMm: roundedWidth,
      lengthMm: roundedLength,
      orientation,
      angleDeg,
      cornerRadiusMm: roundGeometryMm(cornerRadiusMm),
    };
  });
}

export function getManufacturingRule(config: ConfiguratorState) {
  const { machine } = getSelectedOptions(config);
  return getConfiguratorDataForProduct(config.productTypeId).manufacturingRules[
    machine.process as CuttingProcess
  ];
}

export function getEffectiveLimits(config: ConfiguratorState) {
  return getConfiguratorDataForProduct(config.productTypeId).limits;
}

export function buildProductPatch(
  productId: string,
  previous: ConfiguratorState,
): ConfiguratorState {
  const data = getConfiguratorDataForProduct(productId);
  const product = byId(getProductOptions(), productId);
  const defaults = data.defaults as ConfiguratorState;
  const productDefaults = product.defaults ?? {};
  const nextConfig = {
    ...previous,
    ...defaults,
    ...productDefaults,
    productTypeId: product.id,
  };

  return {
    ...nextConfig,
    holePatternId:
      nextConfig.holePatternId ?? product.recommendedPatterns[0] ?? previous.holePatternId,
  };
}

export function getAvailableHolePatterns(config: ConfiguratorState) {
  const data = getConfiguratorDataForProduct(config.productTypeId);
  const { product } = getSelectedOptions(config);
  const recommendedPatterns = data.holePatterns.filter(
    pattern => pattern.id !== 'custom' && product.recommendedPatterns.includes(pattern.id),
  );
  const customPattern = data.holePatterns.find(pattern => pattern.id === 'custom');

  return customPattern ? [...recommendedPatterns, customPattern] : recommendedPatterns;
}

function getShapeAreaMultiplier(config: ConfiguratorState) {
  const { product } = getSelectedOptions(config);
  if (product.visual.kind === 'trapezoid') return 0.82;
  if (product.visual.kind === 'freeform') return 0.92;
  return 1;
}

function getOuterCutLengthM(config: ConfiguratorState) {
  const { product } = getSelectedOptions(config);
  const rectanglePerimeter = 2 * (config.widthMm + config.lengthMm);
  const shapeMultiplier =
    product.visual.kind === 'trapezoid' ? 1.08 : product.visual.kind === 'freeform' ? 1.18 : 1;
  const holePerimeter = getHoleLayout(config).reduce(
    (sum, hole) => sum + getHoleCutLengthMm(hole),
    0,
  );

  return (rectanglePerimeter * shapeMultiplier + holePerimeter) / 1000;
}

export function calculateEstimate(config: ConfiguratorState): ConfiguratorEstimate {
  const { machine, finish, material, product } = getSelectedOptions(config);
  const bendLines = getProductBendLines(config);
  const flatAreaMm2 = config.widthMm * config.lengthMm;
  const areaM2 = (flatAreaMm2 * getShapeAreaMultiplier(config)) / 1_000_000;
  const rawWeightKg = areaM2 * config.thicknessMm * (material.densityKgM3 / 1000);
  const holeAreaM2 =
    getHoleLayout(config).reduce((sum, hole) => sum + getHoleAreaMm2(hole), 0) / 1_000_000;
  const removedWeightKg = holeAreaM2 * config.thicknessMm * (material.densityKgM3 / 1000);
  const weightKg = Math.max(rawWeightKg - removedWeightKg, 0.05);
  const cutLengthM = getOuterCutLengthM(config);
  const cuttingPrice =
    cutLengthM * machine.cutPricePerMeter + config.holeCount * machine.holeCutPrice;
  const bendPrice = bendLines.length > 0 ? machine.bendPrice * bendLines.length : 0;
  const unitPrice =
    (weightKg * material.pricePerKg + cuttingPrice + bendPrice + machine.setupPrice) *
    finish.priceMultiplier;
  const totalWeightKg = weightKg * config.quantity;
  const leadTimeDays = Math.max(
    finish.leadTimeDays,
    bendLines.length > 0 || product.operations.includes('bending') ? 3 : 1,
  );

  return {
    areaM2,
    weightKg,
    totalWeightKg,
    unitPrice,
    totalPrice: unitPrice * config.quantity,
    cutLengthM,
    leadTimeDays,
  };
}

function getDefaultPatternEdgeDistance(config: ConfiguratorState) {
  return Math.min(
    Math.max(
      config.holeDiameterMm * 2,
      config.thicknessMm * 3,
      Math.min(config.widthMm, config.lengthMm) * 0.12,
    ),
    Math.min(config.widthMm, config.lengthMm) * 0.38,
  );
}

function getPatternEdgeDistances(config: ConfiguratorState) {
  const fallback = getDefaultPatternEdgeDistance(config);
  const edgeX = clamp(config.holePatternEdgeXMm ?? fallback, 0, config.widthMm / 2);
  const edgeY = clamp(config.holePatternEdgeYMm ?? fallback, 0, config.lengthMm / 2);

  return { edgeX, edgeY };
}

function getPatternAxisPosition(sizeMm: number, edgeMm: number, count: number, index: number) {
  if (count <= 1) return sizeMm / 2;

  return edgeMm + ((sizeMm - edgeMm * 2) * index) / Math.max(1, count - 1);
}

export function getHoleLayout(config: ConfiguratorState): HolePoint[] {
  if (config.holeCount <= 0) return [];

  const { holePattern } = getSelectedOptions(config);
  if (holePattern.layout === 'none') return [];

  if (holePattern.layout === 'custom') {
    const points = Array.from({ length: config.holeCount }, (_, index) => {
      const override = config.holePositions?.[index];
      const xMm = override?.xMm ?? config.widthMm / 2;
      const yMm = override?.yMm ?? config.lengthMm / 2;

      return {
        xMm,
        yMm,
        xRatio: xMm / Math.max(config.widthMm, 1),
        yRatio: yMm / Math.max(config.lengthMm, 1),
      };
    });

    return withHoleOverrides(config, points);
  }

  let columns =
    holePattern.layout === 'row'
      ? config.holeCount
      : Math.max(1, Math.min(holePattern.columns, config.holeCount));
  let rows =
    holePattern.layout === 'row'
      ? 1
      : Math.max(1, holePattern.rows, Math.ceil(config.holeCount / columns));

  if (config.holePatternOrientation === 'vertical') {
    if (holePattern.layout === 'row') {
      columns = 1;
      rows = config.holeCount;
    } else if (holePattern.layout === 'grid' || holePattern.layout === 'anchor') {
      [columns, rows] = [rows, columns];
    }
  }

  const { edgeX, edgeY } = getPatternEdgeDistances(config);
  const innerWidth = Math.max(config.widthMm - edgeX * 2, 0);
  const innerLength = Math.max(config.lengthMm - edgeY * 2, 0);
  const points: RawHolePoint[] = [];

  if (holePattern.layout === 'triangular') {
    const rowsCount = Math.max(2, rows);
    for (let row = 0; row < rowsCount; row += 1) {
      const rowColumns = row + 1;
      const yMm = edgeY + (innerLength * row) / Math.max(1, rowsCount - 1);
      const rowWidth = innerWidth * ((row + 1) / rowsCount);
      const startX = config.widthMm / 2 - rowWidth / 2;
      for (let col = 0; col < rowColumns; col += 1) {
        if (points.length >= config.holeCount) return withHoleOverrides(config, points);
        const xMm =
          rowColumns === 1 ? config.widthMm / 2 : startX + (rowWidth * col) / (rowColumns - 1);
        points.push({ xMm, yMm, xRatio: xMm / config.widthMm, yRatio: yMm / config.lengthMm });
      }
    }
    return withHoleOverrides(config, points);
  }

  if (holePattern.layout === 'perimeter') {
    for (let index = 0; index < config.holeCount; index += 1) {
      const t = index / Math.max(config.holeCount, 1);
      let xMm = edgeX;
      let yMm = edgeY;
      if (t < 0.25) {
        xMm = edgeX + innerWidth * (t / 0.25);
      } else if (t < 0.5) {
        xMm = config.widthMm - edgeX;
        yMm = edgeY + innerLength * ((t - 0.25) / 0.25);
      } else if (t < 0.75) {
        xMm = config.widthMm - edgeX - innerWidth * ((t - 0.5) / 0.25);
        yMm = config.lengthMm - edgeY;
      } else {
        yMm = config.lengthMm - edgeY - innerLength * ((t - 0.75) / 0.25);
      }
      points.push({ xMm, yMm, xRatio: xMm / config.widthMm, yRatio: yMm / config.lengthMm });
    }
    return withHoleOverrides(config, points);
  }

  if (holePattern.layout === 'top-row') {
    const topColumns = Math.max(1, Math.min(holePattern.columns, config.holeCount));
    const topRows = Math.max(1, Math.ceil(config.holeCount / topColumns));

    for (let row = 0; row < topRows; row += 1) {
      for (let col = 0; col < topColumns; col += 1) {
        if (points.length >= config.holeCount) return withHoleOverrides(config, points);
        const xMm = getPatternAxisPosition(config.widthMm, edgeX, topColumns, col);
        const yMm = edgeY + row * Math.max(config.holeSlotLengthMm, config.holeDiameterMm) * 1.35;
        points.push({
          xMm,
          yMm: Math.min(yMm, config.lengthMm - edgeY),
          xRatio: xMm / config.widthMm,
          yRatio: yMm / config.lengthMm,
        });
      }
    }

    return withHoleOverrides(config, points);
  }

  if (holePattern.layout === 'bent-link-3slot') {
    const edgeY = Math.min(
      Math.max(config.holePatternEdgeYMm ?? getDefaultPatternEdgeDistance(config), 0),
      config.lengthMm / 2,
    );
    const basePoints = [
      { xMm: config.widthMm / 2, yMm: edgeY },
      { xMm: config.widthMm / 2, yMm: config.lengthMm / 2 },
      { xMm: config.widthMm / 2, yMm: config.lengthMm - edgeY },
    ];

    return withHoleOverrides(
      config,
      basePoints.slice(0, config.holeCount).map(point => ({
        ...point,
        xRatio: point.xMm / config.widthMm,
        yRatio: point.yMm / config.lengthMm,
      })),
    );
  }

  if (holePattern.layout === 'double-link-6slot') {
    const edgeX = Math.min(
      Math.max(config.holePatternEdgeXMm ?? getDefaultPatternEdgeDistance(config), 0),
      config.widthMm / 2,
    );
    const edgeY = Math.min(
      Math.max(config.holePatternEdgeYMm ?? getDefaultPatternEdgeDistance(config), 0),
      config.lengthMm / 2,
    );
    const leftX = edgeX;
    const rightX = config.widthMm - edgeX;
    const middleY = config.lengthMm * 0.47;
    const bottomY = config.lengthMm - edgeY;
    const basePoints = [
      { xMm: leftX, yMm: edgeY },
      { xMm: rightX, yMm: edgeY },
      { xMm: leftX, yMm: middleY },
      { xMm: rightX, yMm: middleY },
      { xMm: leftX, yMm: bottomY },
      { xMm: rightX, yMm: bottomY },
    ];

    return withHoleOverrides(
      config,
      basePoints.slice(0, config.holeCount).map(point => ({
        ...point,
        xRatio: point.xMm / config.widthMm,
        yRatio: point.yMm / config.lengthMm,
      })),
    );
  }

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < columns; col += 1) {
      if (points.length >= config.holeCount) return withHoleOverrides(config, points);
      const xMm = getPatternAxisPosition(config.widthMm, edgeX, columns, col);
      const yMm = getPatternAxisPosition(config.lengthMm, edgeY, rows, row);
      points.push({
        xMm,
        yMm,
        xRatio: xMm / config.widthMm,
        yRatio: yMm / config.lengthMm,
      });
    }
  }

  return withHoleOverrides(config, points);
}

export function getGeometryCheck(config: ConfiguratorState): GeometryCheck {
  const { product, machine, material } = getSelectedOptions(config);
  const processRule = getManufacturingRule(config);
  const bendRule = getConfiguratorDataForProduct(config.productTypeId).manufacturingRules.bending;
  const holes = getHoleLayout(config);
  const bendLines = getProductBendLines(config);
  const minRequiredHoleMm = Math.max(3, config.thicknessMm * processRule.minHoleDiameterMultiplier);
  const maxHoleDiameterMm = Math.max(config.holeDiameterMm, ...holes.map(hole => hole.diameterMm));
  const minRequiredEdgeMm = Math.max(
    maxHoleDiameterMm * processRule.minEdgeDistanceMultiplier,
    config.thicknessMm * 2,
    3,
  );
  const minRequiredPitchMm = Math.max(
    config.thicknessMm * 1.5,
    'kerfMm' in processRule ? processRule.kerfMm * 6 : 3,
    3,
  );
  const warnings: string[] = [];

  if (
    config.thicknessMm < machine.supportedThicknessMm[0] ||
    config.thicknessMm > machine.supportedThicknessMm[1]
  ) {
    warnings.push(
      `Vybraný stroj podporuje hrúbku ${machine.supportedThicknessMm[0]}-${machine.supportedThicknessMm[1]} mm.`,
    );
  }
  if (!machine.supportedOperations.some(operation => product.operations.includes(operation))) {
    warnings.push('Vybraný stroj nepokrýva odporúčané operácie pre tento typ dielca.');
  }
  if (holes.some(hole => hole.diameterMm < minRequiredHoleMm)) {
    warnings.push(
      `Niektoré otvory sú pod odporúčaným minimom ${Math.ceil(minRequiredHoleMm)} mm pre zvolený proces.`,
    );
  }
  if (
    config.thicknessMm < material.recommendedThicknessMm[0] ||
    config.thicknessMm > material.recommendedThicknessMm[1]
  ) {
    warnings.push(
      `Materiál má odporúčanú hrúbku ${material.recommendedThicknessMm[0]}-${material.recommendedThicknessMm[1]} mm.`,
    );
  }
  if (bendLines.length > 0) {
    const minDistanceToBendMm = Math.max(
      config.thicknessMm * bendRule.minFlangeMultiplier,
      maxHoleDiameterMm * bendRule.holeToBendMultiplier,
    );
    if (
      config.holeCount > 0 &&
      holes.some(hole =>
        bendLines.some(line => {
          const distance =
            line.orientation === 'horizontal'
              ? Math.abs(hole.yMm - line.positionMm)
              : Math.abs(hole.xMm - line.positionMm);
          return distance < minDistanceToBendMm;
        }),
      )
    ) {
      warnings.push('Otvory sú pravdepodobne príliš blízko ohybu.');
    }
  }

  if (!holes.length) {
    return {
      edgeDistanceMm: 0,
      pitchXMm: null,
      pitchYMm: null,
      minRequiredEdgeMm,
      minRequiredPitchMm,
      minRequiredHoleMm,
      manufacturabilityScore: Math.max(0, 100 - warnings.length * 18),
      warnings,
    };
  }

  const edgeDistanceMm = Math.min(
    ...holes.flatMap(hole => {
      const extents = getHoleHalfExtents(hole);
      return [
        hole.xMm - extents.xMm,
        config.widthMm - hole.xMm - extents.xMm,
        hole.yMm - extents.yMm,
        config.lengthMm - hole.yMm - extents.yMm,
      ];
    }),
  );
  let nearestHoleClearanceMm: number | null = null;

  for (let index = 0; index < holes.length; index += 1) {
    for (let nextIndex = index + 1; nextIndex < holes.length; nextIndex += 1) {
      const first = holes[index];
      const second = holes[nextIndex];
      const centerDistanceMm = Math.hypot(first.xMm - second.xMm, first.yMm - second.yMm);
      const firstExtent = Math.max(getHoleHalfExtents(first).xMm, getHoleHalfExtents(first).yMm);
      const secondExtent = Math.max(getHoleHalfExtents(second).xMm, getHoleHalfExtents(second).yMm);
      const clearanceMm = centerDistanceMm - firstExtent - secondExtent;
      nearestHoleClearanceMm =
        nearestHoleClearanceMm === null
          ? clearanceMm
          : Math.min(nearestHoleClearanceMm, clearanceMm);
    }
  }

  const pitchXMm = nearestHoleClearanceMm;
  const pitchYMm = nearestHoleClearanceMm;

  if (edgeDistanceMm < minRequiredEdgeMm) {
    warnings.push(
      `Otvory sú príliš blízko okraja, odporúčané minimum je ${Math.ceil(minRequiredEdgeMm)} mm.`,
    );
  }
  if (nearestHoleClearanceMm !== null && nearestHoleClearanceMm < minRequiredPitchMm) {
    warnings.push(
      nearestHoleClearanceMm < 0
        ? 'Niektoré otvory sa prekrývajú.'
        : `Medzi niektorými otvormi ostáva menej materiálu než odporúčané minimum ${Math.ceil(minRequiredPitchMm)} mm.`,
    );
  }

  return {
    edgeDistanceMm,
    pitchXMm,
    pitchYMm,
    minRequiredEdgeMm,
    minRequiredPitchMm,
    minRequiredHoleMm,
    manufacturabilityScore: Math.max(0, 100 - warnings.length * 18),
    warnings,
  };
}

export function formatNumber(value: number, fractionDigits = 1) {
  return new Intl.NumberFormat('sk-SK', {
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

export function formatWeight(valueKg: number, fractionDigits = 2) {
  if (Math.abs(valueKg) < 1) {
    return `${formatNumber(valueKg * 1000, 0)} g`;
  }

  return `${new Intl.NumberFormat('sk-SK', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(valueKg)} kg`;
}

export function formatCurrency(value: number) {
  return new Intl.NumberFormat('sk-SK', {
    style: 'currency',
    currency: CONFIGURATOR_CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function clampToLimit(key: LimitKey, value: number, config?: ConfiguratorState) {
  const limit = config ? getEffectiveLimits(config)[key] : getConfiguratorData().limits[key];
  return Math.min(Math.max(value, limit.min), limit.max);
}
